/** Hex id without optional API-style prefixes (e.g. `view_`). */
const HEX16 = /^[0-9a-fA-F]{16}$/;

function stripOptionalPrefixes(id: string): string {
	return id
		.replace(/^view_/i, '')
		.replace(/^page_/i, '')
		.replace(/^account_/i, '');
}

const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function decodeCrockfordBase32ToInt(str: string): bigint {
	let s = str.toUpperCase().replace(/-/g, '');
	s = s.replace(/[O0]/g, '0').replace(/[IL1]/g, '1');

	let value = BigInt(0);
	for (const c of s) {
		const v = CROCKFORD_ALPHABET.indexOf(c);
		if (v === -1) {
			throw new Error('Invalid base32 char: ' + c);
		}
		value = value * BigInt(32) + BigInt(v);
	}
	return value;
}

function intToBytesBigEndian(intVal: bigint, byteLength: number): Uint8Array {
	const bytes: number[] = [];
	let v = intVal;
	for (let i = byteLength - 1; i >= 0; i--) {
		bytes[i] = Number(v & BigInt(0xff));
		v >>= BigInt(8);
	}
	return Uint8Array.from(bytes);
}

/**
 * Decodes the Crockford Base32 **report id** path segment into 16-byte hex account and view ids.
 * Letter case is ignored (typically use lowercase URLs; {@link encodeReportId} emits lowercase).
 * Used by the analytics app router (`/:reportId`).
 */
export function decodeReportId(encoded: string): {
	accountId: string;
	viewId: string;
} {
	const intVal = decodeCrockfordBase32ToInt(encoded);
	const bytes = intToBytesBigEndian(intVal, 16);
	const hex = Array.from(bytes)
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');

	return {
		accountId: hex.slice(0, 16),
		viewId: hex.slice(16, 32),
	};
}

/**
 * Minimum length for a Crockford report-id path segment.
 * Full 128-bit ids encode to ~26 chars; app routes like `clients` are shorter and
 * must not be treated as report ids (they Base32-decode to near-zero garbage).
 */
export const MIN_REPORT_ID_PATH_SEGMENT_LENGTH = 16;

/**
 * Best-effort decode of an analytics shared-report path segment (`/{reportId}`).
 * Returns null for short app routes, storybook paths, invalid Base32, or an
 * all-zero account id (typical false positive from short Crockford words).
 */
export function tryDecodeReportIdPathSegment(
	segment: string
): { accountId: string; viewId: string } | null {
	const reportId = segment?.trim();
	if (!reportId) {
		return null;
	}
	if (
		reportId.includes('iframe') ||
		reportId.includes('storybook') ||
		reportId.length < MIN_REPORT_ID_PATH_SEGMENT_LENGTH
	) {
		return null;
	}
	try {
		const decoded = decodeReportId(reportId);
		if (/^0+$/.test(decoded.accountId)) {
			return null;
		}
		return decoded;
	} catch {
		return null;
	}
}

/**
 * Resolves account/view ids from a share URL path segment, falling back to
 * `?reportId=` (used by `/verify` email-code links on the analytics host).
 */
export function resolveEncodedReportIds(
	pathSegment: string | null | undefined,
	queryReportId?: string | null
): {
	reportId: string;
	accountId: string;
	viewId: string;
} | null {
	const pathDecoded = tryDecodeReportIdPathSegment(pathSegment ?? '');
	if (pathDecoded && pathSegment?.trim()) {
		return {
			reportId: pathSegment.trim().toLowerCase(),
			...pathDecoded,
		};
	}

	const queryDecoded = tryDecodeReportIdPathSegment(queryReportId ?? '');
	if (queryDecoded && queryReportId?.trim()) {
		return {
			reportId: queryReportId.trim().toLowerCase(),
			...queryDecoded,
		};
	}

	return null;
}

/**
 * Validates and normalizes 16-character hex account and view ids used in report encoding.
 */
function normalizeReportHexIds(
	accountId: string,
	viewId: string
): { accountId: string; viewId: string } {
	const a = stripOptionalPrefixes(accountId).toLowerCase();
	const v = stripOptionalPrefixes(viewId).toLowerCase();
	if (!HEX16.test(a) || !HEX16.test(v)) {
		throw new Error(
			'accountId and viewId must each be 16 hexadecimal characters'
		);
	}
	return { accountId: a, viewId: v };
}

/** True when two view ids match, ignoring optional `view_` prefix (any casing). */
export function viewIdsMatch(
	left: string | null | undefined,
	right: string | null | undefined
): boolean {
	if (!left?.trim() || !right?.trim()) {
		return false;
	}
	const normalize = (id: string) =>
		id
			.trim()
			.replace(/^view_/i, '')
			.toLowerCase();
	return normalize(left) === normalize(right);
}

/**
 * AppSync Events channel for realtime card data on a report view.
 * Ids must be 16-char hex without API prefixes (`view_`, `account_`).
 */
export function buildAnalyticsEventsChannel(
	accountId: string,
	viewId: string
): string | null {
	const a = stripOptionalPrefixes(accountId).toLowerCase();
	const v = stripOptionalPrefixes(viewId).toLowerCase();
	if (!HEX16.test(a) || !HEX16.test(v)) {
		return null;
	}
	return `analytics/${a}/${v}`;
}

/** Error message when account/view ids cannot form an AppSync events channel. */
export const INVALID_ANALYTICS_EVENTS_CHANNEL_MESSAGE =
	'Invalid analytics events channel ids';

/**
 * Like {@link buildAnalyticsEventsChannel} but throws when ids are invalid.
 */
export function requireAnalyticsEventsChannel(
	accountId: string,
	viewId: string
): string {
	const channel = buildAnalyticsEventsChannel(accountId, viewId);
	if (!channel) {
		throw new Error(INVALID_ANALYTICS_EVENTS_CHANNEL_MESSAGE);
	}
	return channel;
}

function encodeCrockfordBase32(intVal: bigint): string {
	if (intVal === 0n) {
		return '0';
	}
	let s = '';
	let v = intVal;
	while (v > 0n) {
		const rem = v % 32n;
		s = CROCKFORD_ALPHABET[Number(rem)] + s;
		v = v / 32n;
	}
	return s.toLowerCase();
}

/**
 * Encodes account + view hex ids into the Crockford Base32 **report id** path segment
 * used by the analytics app (`/:reportId`). Always **lowercase** (letters `a-z` plus digits).
 * {@link decodeReportId} accepts any case.
 */
export function encodeReportId(accountId: string, viewId: string): string {
	const { accountId: a, viewId: v } = normalizeReportHexIds(
		accountId,
		viewId
	);
	const hex = a + v;
	const bytes = new Uint8Array(16);
	for (let i = 0; i < 16; i++) {
		bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
	}
	let intVal = 0n;
	for (let i = 0; i < 16; i++) {
		intVal = (intVal << 8n) + BigInt(bytes[i]);
	}
	return encodeCrockfordBase32(intVal);
}
